<template>
  <div class="admin-invite-accept-page">
    <v-container class="py-10" max-width="600">
      <div class="page-header mb-8 text-center">
        <router-link to="/">
          <PassingOnLogo class="brand-logo-svg" />
        </router-link>
      </div>

      <!-- Loading -->
      <div v-if="loadingInvite" class="text-center py-10">
        <v-progress-circular indeterminate color="primary" size="48" />
        <p class="mt-4 text-body-1">Loading invitation...</p>
      </div>

      <!-- Invalid / already accepted -->
      <v-card v-else-if="inviteError" class="section-card" elevation="0">
        <v-card-text class="pa-8 text-center">
          <v-icon size="64" color="error" class="mb-4">mdi-alert-circle</v-icon>
          <h2 class="text-h5 mb-3">Invitation Not Available</h2>
          <p class="text-body-1 mb-6">{{ inviteError }}</p>
          <v-btn color="primary" variant="outlined" to="/">Go to Home</v-btn>
        </v-card-text>
      </v-card>

      <!-- Invite info + auth -->
      <template v-else-if="invite">
        <v-card class="section-card mb-6" elevation="0">
          <v-card-text class="pa-8 text-center">
            <v-icon size="64" color="primary" class="mb-4">mdi-shield-account</v-icon>
            <h2 class="text-h5 mb-2">Administrator Invitation</h2>
            <p class="text-body-1 mb-1">
              <span v-if="invite.invitedBy">{{ invite.invitedBy }} has invited you</span>
              <span v-else>You've been invited</span>
              to help manage the memory for
            </p>
            <p class="text-h6 primary--text mb-4" style="color: rgb(var(--v-theme-primary))">
              {{ invite.memorialName }}
            </p>
            <p class="text-body-2 text-medium-emphasis">
              This invitation was sent to <strong>{{ invite.email }}</strong>
            </p>
          </v-card-text>
        </v-card>

        <!-- Already logged in with correct email -->
        <v-card v-if="isLoggedInWithCorrectEmail" class="section-card mb-6" elevation="0">
          <v-card-text class="pa-8 text-center">
            <p class="text-body-1 mb-6">
              You're signed in as <strong>{{ currentUserEmail }}</strong>. Click below to accept this invitation and become an administrator.
            </p>
            <v-alert v-if="acceptError" type="error" variant="tonal" class="mb-4">{{ acceptError }}</v-alert>
            <v-alert v-if="acceptSuccess" type="success" variant="tonal" class="mb-4">{{ acceptSuccess }}</v-alert>
            <div v-if="!acceptSuccess">
              <v-btn
                color="primary"
                variant="outlined"
                size="large"
                :loading="accepting"
                @click="acceptInvite"
              >
                Accept Invitation
              </v-btn>
            </div>
            <div v-else>
              <v-btn color="primary" variant="outlined" @click="goToDashboard">
                Show Memory
              </v-btn>
            </div>
          </v-card-text>
        </v-card>

        <!-- Logged in with wrong email -->
        <v-card v-else-if="isLoggedIn && !isLoggedInWithCorrectEmail" class="section-card mb-6" elevation="0">
          <v-card-text class="pa-8 text-center">
            <v-alert type="warning" variant="tonal" class="mb-4">
              You're signed in as <strong>{{ currentUserEmail }}</strong>, but this invitation was sent to <strong>{{ invite.email }}</strong>. Click below to sign out and continue with the invited email address.
            </v-alert>
            <v-btn color="primary" variant="outlined" @click="signOutAndContinue">
              Sign Out &amp; Continue
            </v-btn>
          </v-card-text>
        </v-card>

        <!-- Not logged in: show sign in / sign up -->
        <v-card v-else class="section-card mb-6" elevation="0">
          <v-card-title class="section-header primary-header">
            <v-tabs v-model="authTab" color="primary" grow>
              <v-tab value="signin">Sign In</v-tab>
              <v-tab value="signup">Create Account</v-tab>
            </v-tabs>
          </v-card-title>
          <v-card-text class="pa-6">
            <v-alert v-if="authError" type="error" variant="tonal" class="mb-4">{{ authError }}</v-alert>

            <v-window v-model="authTab">
              <!-- Sign In -->
              <v-window-item value="signin">
                <p class="text-body-2 text-medium-emphasis mb-4">
                  Sign in with your account at <strong>{{ invite.email }}</strong> to accept this invitation.
                </p>
                <v-text-field
                  v-model="authEmail"
                  label="Email"
                  type="email"
                  variant="outlined"
                  density="comfortable"
                  class="mb-3"
                />
                <v-text-field
                  v-model="authPassword"
                  label="Password"
                  type="password"
                  variant="outlined"
                  density="comfortable"
                  class="mb-4"
                  @keyup.enter="signIn"
                />
                <v-btn
                  color="primary"
                  variant="outlined"
                  block
                  :loading="authLoading"
                  @click="signIn"
                >
                  Sign In &amp; Accept
                </v-btn>
              </v-window-item>

              <!-- Sign Up -->
              <v-window-item value="signup">
                <p class="text-body-2 text-medium-emphasis mb-4">
                  Create a new account with <strong>{{ invite.email }}</strong> to accept this invitation.
                </p>
                <v-text-field
                  v-model="signupFirstName"
                  label="First name"
                  variant="outlined"
                  density="comfortable"
                  class="mb-3"
                />
                <v-text-field
                  v-model="authEmail"
                  label="Email"
                  type="email"
                  variant="outlined"
                  density="comfortable"
                  class="mb-3"
                />
                <v-text-field
                  v-model="authPassword"
                  label="Password"
                  type="password"
                  variant="outlined"
                  density="comfortable"
                  class="mb-4"
                  @keyup.enter="signUp"
                />
                <v-btn
                  color="primary"
                  variant="outlined"
                  block
                  :loading="authLoading"
                  @click="signUp"
                >
                  Create Account &amp; Accept
                </v-btn>
              </v-window-item>
            </v-window>
          </v-card-text>
        </v-card>
      </template>
    </v-container>
  </div>
</template>

<script>
import { useMemorialStore } from '@/stores/memorial'
import api from '@/services/api'
import PassingOnLogo from '@/components/PassingOnLogo.vue'

export default {
  name: 'AdminInviteAcceptPage',
  components: { PassingOnLogo },

  props: {
    token: {
      type: String,
      required: true
    }
  },

  data() {
    return {
      loadingInvite: true,
      invite: null,
      inviteError: '',
      authTab: 'signup',
      authEmail: '',
      authPassword: '',
      signupFirstName: '',
      authLoading: false,
      authError: '',
      accepting: false,
      acceptError: '',
      acceptSuccess: '',
      acceptedMemorialUuid: null
    }
  },

  computed: {
    store() {
      return useMemorialStore()
    },
    isLoggedIn() {
      return !!this.store.apiToken
    },
    currentUserEmail() {
      return this.store.userEmail || this.store.user?.email || ''
    },
    isLoggedInWithCorrectEmail() {
      if (!this.isLoggedIn || !this.invite) return false
      return this.currentUserEmail?.toLowerCase() === this.invite.email?.toLowerCase()
    }
  },

  async mounted() {
    await this.loadInviteInfo()
    // Pre-fill email for sign-in / sign-up forms
    if (this.invite?.email) {
      this.authEmail = this.invite.email
    }
  },

  methods: {
    async loadInviteInfo() {
      this.loadingInvite = true
      try {
        const result = await api.getAdminInviteInfo(this.token)
        if (result.success) {
          this.invite = result.invite
        } else {
          this.inviteError = result.msg || 'This invitation is invalid or has already been accepted.'
        }
      } catch (e) {
        this.inviteError = 'Failed to load invitation details.'
      } finally {
        this.loadingInvite = false
      }
    },

    async acceptInvite() {
      this.accepting = true
      this.acceptError = ''
      try {
        const result = await api.acceptAdminInvite(this.token, this.store.apiToken)
        if (result.success) {
          this.acceptSuccess = result.msg || 'You are now an administrator of this memory.'
          this.acceptedMemorialUuid = result.memorialUuid
          // Load the memorial into the store so they can access the dashboard
          if (result.memorialUuid) {
            this.store.memorialUuid = result.memorialUuid
          }
        } else {
          this.acceptError = result.msg || 'Failed to accept invitation.'
        }
      } catch (e) {
        this.acceptError = 'An error occurred. Please try again.'
      } finally {
        this.accepting = false
      }
    },

    async signIn() {
      this.authLoading = true
      this.authError = ''
      try {
        const result = await api.signIn(this.authEmail, this.authPassword)
        if (result.success && result.token) {
          this.store.setApiAuth(result.token, result.user || {})
          await this.acceptInvite()
          // No auto-redirect — the template will show the success state with a "Show Memory" button
        } else {
          this.authError = result.msg || 'Sign in failed. Please check your email and password.'
        }
      } catch (e) {
        this.authError = 'An error occurred during sign in.'
      } finally {
        this.authLoading = false
      }
    },

    async signUp() {
      this.authLoading = true
      this.authError = ''
      try {
        const result = await api.signUp(this.authEmail, this.authPassword, null, this.signupFirstName)
        if (result.token) {
          this.store.setApiAuth(result.token, result.user || { email: this.authEmail })
          await this.acceptInvite()
          // No auto-redirect — the template will show the success state with a "Show Memory" button
        } else {
          this.authError = result.msg || 'Account creation failed. This email may already be registered — try signing in.'
        }
      } catch (e) {
        this.authError = 'An error occurred during account creation.'
      } finally {
        this.authLoading = false
      }
    },

    signOutAndContinue() {
      // Clear auth state without redirecting away from this page
      api.clearToken()
      try {
        this.store.clearAllData()
      } catch (e) {
        // store may not be fully initialized
      }
      localStorage.clear()
      sessionStorage.clear()
      // Pre-fill email so the form is ready for the invited address
      if (this.invite?.email) {
        this.authEmail = this.invite.email
      }
    },

    goToDashboard() {
      if (this.acceptedMemorialUuid) {
        this.$router.push(`/memory/load/${this.acceptedMemorialUuid}`)
      } else {
        this.$router.push('/memory/dashboard')
      }
    }
  }
}
</script>

<style scoped>
.page-header {
  margin-bottom: 24px;
}

.brand-logo-svg {
  height: 50px;
}

.section-card {
  border: 1px solid rgba(0,0,0,0.08);
  border-radius: 8px;
  overflow: hidden;
}

.section-header {
  background: white;
  padding: 0;
}

.primary-header {
  background: white;
}
</style>
